import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Clock,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileText,
  HeartHandshake,
  Play,
  VolumeX,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DatePicker from "@/components/custom/DatePicker";
import { cn, formatPhoneInput, handlePhoneInputChange, handlePhoneKeyDown } from "@/lib/utils";
import AuthService from "@/lib/auth";

const leadFormSchema = z
  .object({
    first_name: z
      .string()
      .min(1, "First name is required")
      .regex(/^[A-Za-z\s'-]+$/, "First name should not contain numbers or special characters"),
    last_name: z
      .string()
      .min(1, "Last name is required")
      .regex(/^[A-Za-z\s'-]+$/, "Last name should not contain numbers or special characters"),
    middle_name: z.string().optional(),
    social_security_number: z
      .string()
      .optional()
      .refine((val) => !val || /^\d{3}-\d{2}-\d{4}$/.test(val), "SSN must be in format 123-45-6789"),
    date_of_birth: z
      .date({ required_error: "Date of birth is required" })
      .max(new Date(), "Date of birth cannot be in the future"),
    gender: z.string().min(1, "Gender is required"),
    mobile_number: z
      .string()
      .min(1, "Mobile number is required")
      .refine(
        (v) => {
          let digits = v.replace(/\D/g, "");
          if (digits.length === 11 && digits.startsWith("1")) {
            digits = digits.slice(1);
          }
          return digits.length === 10;
        },
        "Mobile number must be a valid 10-digit number"
      ),
    email: z
      .union([z.literal(""), z.string().email("Invalid email address")])
      .optional(),
    address_line1: z.string().min(1, "Address Line 1 is required"),
    address_line2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    state: z.string().min(1, "State is required"),
    zip: z
      .string()
      .min(1, "ZIP Code is required")
      .regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code (e.g. 12345 or 12345-6789)"),
    emergency_contact_name: z.string().optional(),
    emergency_contact_phone: z.string().optional(),
    emergency_contact_relationship: z.string().optional(),
    race: z.string().optional(),
    ethnicity: z.string().optional(),
    preferred_language: z.string().optional(),
    // Consents
    hipaa_consent: z.boolean().refine((val) => val === true, {
      message: "You must accept the HIPAA privacy practices to proceed",
    }),
    esign_consent: z.boolean().refine((val) => val === true, {
      message: "You must accept the e-signature terms to proceed",
    }),
    mhai_consent: z.boolean().refine((val) => val === true, {
      message: "You must acknowledge and accept the MonitorHealth terms to proceed",
    }),
    sms_consent: z.boolean().refine((val) => val === true, {
      message: "You must consent to SMS communications to proceed",
    }),
    biometric_consent: z.boolean().optional(),
    email_consent: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.email && data.email.trim() !== "" && !data.email_consent) {
        return false;
      }
      return true;
    },
    {
      message: "You must consent to email communications when providing an email",
      path: ["email_consent"],
    }
  );

type LeadFormValues = z.infer<typeof leadFormSchema>;

export default function LeadPatientRegistration() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkStatus, setLinkStatus] = useState<
    "active" | "expired" | "already_submitted" | "cancelled" | "error" | "submitted_now"
  >("active");
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [secondsRemaining, setSecondsRemaining] = useState<number>(600);
  const [leadName, setLeadName] = useState<string>("");
  const [submittedPatientName, setSubmittedPatientName] = useState<string>("");

  // Intro video gate for patient self-registration
  const PATIENT_INTRO_VIDEO_URL =
    (window as any)._env_?.VITE_PATIENT_INTRO_VIDEO_URL ||
    import.meta.env.VITE_PATIENT_INTRO_VIDEO_URL ||
    "https://d4ilgen09xf69.cloudfront.net/patient_onboarding_video_compressed.mp4";

  const [showVideoIntro, setShowVideoIntro] = useState(true);
  const videoIntroRef = useRef<HTMLVideoElement>(null);
  const [videoNeedsUnmute, setVideoNeedsUnmute] = useState(false);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);

  useEffect(() => {
    if (!showVideoIntro || linkStatus !== "active") return;
    const vid = videoIntroRef.current;
    if (!vid) return;

    vid.muted = false;
    const playAttempt = vid.play();
    if (playAttempt !== undefined) {
      playAttempt.catch(() => {
        vid.muted = true;
        setVideoNeedsUnmute(true);
        vid.play().catch(() => {});
      });
    }
  }, [showVideoIntro, linkStatus]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<LeadFormValues>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      first_name: "",
      middle_name: "",
      last_name: "",
      gender: "",
      mobile_number: "",
      email: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip: "",
      hipaa_consent: false,
      esign_consent: false,
      mhai_consent: false,
      sms_consent: false,
      biometric_consent: false,
      email_consent: false,
    },
  });

  const selectedDob = watch("date_of_birth");
  const hipaaChecked = watch("hipaa_consent");
  const esignChecked = watch("esign_consent");
  const mhaiChecked = watch("mhai_consent");
  const smsChecked = watch("sms_consent");
  const biometricChecked = watch("biometric_consent");
  const emailChecked = watch("email_consent");
  const emailVal = watch("email");

  // Load public registration link details
  useEffect(() => {
    if (!token) {
      setLinkStatus("error");
      setStatusMessage("Invalid registration link. Token is missing.");
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        setLoading(true);
        const res: any = await AuthService.getPublicRegistrationData(token);
        const d = res?.data || res;
        if (d && d.status) {
          const leadFullName = [d.first_name, d.middle_name, d.last_name].filter(Boolean).join(" ");
          setLeadName(leadFullName);

          if (d.status === "active") {
            setLinkStatus("active");
            setSecondsRemaining(d.seconds_remaining || 600);

            // Pre-fill phone/email or existing data if available
            if (d.phone) setValue("mobile_number", formatPhoneInput(d.phone), { shouldValidate: true });
            if (d.email) setValue("email", d.email, { shouldValidate: true });

            if (d.first_name) setValue("first_name", d.first_name, { shouldValidate: true });
            if (d.middle_name) setValue("middle_name", d.middle_name, { shouldValidate: true });
            if (d.last_name) setValue("last_name", d.last_name, { shouldValidate: true });

            // If re-editing previous data
            if (d.existing_data) {
              const ex = d.existing_data;
              if (ex.mobile_number) setValue("mobile_number", formatPhoneInput(ex.mobile_number));
              if (ex.first_name) setValue("first_name", ex.first_name);
              if (ex.middle_name) setValue("middle_name", ex.middle_name);
              if (ex.last_name) setValue("last_name", ex.last_name);
              if (ex.gender) setValue("gender", ex.gender);
              if (ex.date_of_birth) setValue("date_of_birth", new Date(ex.date_of_birth));
              if (ex.address_line1) setValue("address_line1", ex.address_line1);
              if (ex.address_line2) setValue("address_line2", ex.address_line2);
              if (ex.city) setValue("city", ex.city);
              if (ex.state) setValue("state", ex.state);
              if (ex.zip) setValue("zip", ex.zip);
              if (ex.social_security_number) setValue("social_security_number", ex.social_security_number);
              if (ex.emergency_contact_name) setValue("emergency_contact_name", ex.emergency_contact_name);
              if (ex.emergency_contact_phone) setValue("emergency_contact_phone", ex.emergency_contact_phone);
              if (ex.hipaa_consent) setValue("hipaa_consent", true);
              if (ex.esign_consent) setValue("esign_consent", true);
              if (ex.mhai_consent) setValue("mhai_consent", true);
              if (ex.sms_consent) setValue("sms_consent", true);
              if (ex.biometric_consent) setValue("biometric_consent", true);
              if (ex.email_consent) setValue("email_consent", true);
            }
          } else {
            setLinkStatus(d.status || "expired");
            setStatusMessage(d.message || "This registration link is no longer active.");
          }
        }
      } catch (err: any) {
        setLinkStatus("error");
        setStatusMessage(err.message || "Unable to load registration link. Please check the URL.");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [token, setValue]);

  // Live 10-minute countdown timer
  useEffect(() => {
    if (linkStatus !== "active" || secondsRemaining <= 0) return;

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setLinkStatus("expired");
          setStatusMessage("This registration link has expired (10-minute limit). Please ask your care representative on call to send you a fresh link.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [linkStatus, secondsRemaining]);

  const formatTimer = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const onSubmit = async (values: LeadFormValues) => {
    if (!token) return;
    try {
      setIsSubmitting(true);
      const cleanPhone = `+1${values.mobile_number.replace(/\D/g, "").replace(/^1/, "").slice(-10)}`;
      const cleanEmail = values.email?.trim() || null;

      const payload = {
        ...values,
        biometric_consent: !!values.biometric_consent,
        mobile_number: cleanPhone,
        email: cleanEmail,
        date_of_birth: format(values.date_of_birth, "yyyy-MM-dd"),
        submitted_at: new Date().toISOString(),
      };

      const fullName = [values.first_name, values.last_name].filter(Boolean).join(" ").trim();
      if (fullName) {
        setSubmittedPatientName(fullName);
      }

      const res: any = await AuthService.submitPublicRegistration(token, payload);
      if (res?.success || res?.data?.success) {
        setLinkStatus("submitted_now");
        toast.success("Registration submitted successfully!");
      } else {
        toast.error("Failed to submit registration. Please try again.");
      }
    } catch (err: any) {
      toast.error(err.message || "Submission failed. Please check with your care coordinator.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // SSN formatting helper
  const handleSsnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 9);
    let formatted = raw;
    if (raw.length > 5) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3, 5)}-${raw.slice(5)}`;
    } else if (raw.length > 3) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3)}`;
    }
    setValue("social_security_number", formatted);
  };

  // Phone formatting helper
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePhoneInputChange(e, (formatted) => {
      setValue("mobile_number", formatted, { shouldDirty: true, shouldValidate: true });
      if (errors.mobile_number) {
        clearErrors("mobile_number");
      }
    }, formatPhoneInput);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f4fbf7] to-gray-50 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-[#1a7a4a] border-t-transparent rounded-full animate-spin mx-auto" />
          <h2 className="text-xl font-semibold text-gray-800">Verifying Registration Link...</h2>
          <p className="text-sm text-gray-500">Preparing secure patient portal</p>
        </div>
      </div>
    );
  }

  // Submitted Now (Success View)
  if (linkStatus === "submitted_now") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f4fbf7] to-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden text-center p-8 sm:p-10">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3">
            Registration Submitted!
          </h1>
          <p className="text-gray-600 mb-6 text-base leading-relaxed">
            Thank you, <span className="font-semibold text-gray-800">{submittedPatientName || leadName || "Patient"}</span>. Your basic information and consents have been safely sent to your care coordinator on call.
          </p>
          <div className="bg-[#f0fdf4] border border-green-200 rounded-2xl p-4 mb-6 text-left text-sm text-green-900 space-y-2">
            <div className="flex items-center gap-2 font-semibold">
              <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>Next Steps with Care Representative</span>
            </div>
            <p className="text-xs text-green-800 leading-relaxed">
              Please inform the representative on your call that you have submitted the form. They will now complete your provider details and finalize your registration.
            </p>
          </div>
          <div className="text-xs text-gray-400">
            For security, this link is now finalized and cannot be reused.
          </div>
        </Card>
      </div>
    );
  }

  // Expired / Already Submitted / Cancelled Screen
  if (linkStatus !== "active") {
    const isExpired = linkStatus === "expired";
    const isAlready = linkStatus === "already_submitted";

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden p-8 sm:p-10 text-center">
          <div className={cn(
            "w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6",
            isExpired ? "bg-amber-100" : isAlready ? "bg-blue-100" : "bg-red-100"
          )}>
            {isExpired && <Clock className="w-10 h-10 text-amber-600" />}
            {isAlready && <FileText className="w-10 h-10 text-blue-600" />}
            {!isExpired && !isAlready && <AlertCircle className="w-10 h-10 text-red-600" />}
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {isExpired && "Registration Link Expired"}
            {isAlready && "Registration Already Submitted"}
            {!isExpired && !isAlready && "Link Unavailable"}
          </h1>

          <p className="text-gray-600 text-sm sm:text-base mb-6 leading-relaxed">
            {statusMessage ||
              (isExpired
                ? "This 10-minute link has expired for your privacy."
                : isAlready
                ? "This registration form has already been submitted and allows one-time submission."
                : "This registration link is no longer valid.")}
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-left text-sm text-amber-900 mb-6 space-y-1">
            <div className="flex items-center gap-2 font-semibold text-amber-800">
              <Phone className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span>Are you on a phone call with staff?</span>
            </div>
            <p className="text-xs text-amber-700">
              Simply ask your care coordinator on call to click <strong>&quot;Resend New Link&quot;</strong> and they will instantly text or email you a fresh 10-minute link so you can complete or edit your details.
            </p>
          </div>

          <div className="text-xs text-gray-400">
            MonitorHealth AI &bull; Secure Patient Onboarding
          </div>
        </Card>
      </div>
    );
  }

  // Active Registration Form View
  const isUrgent = secondsRemaining < 120; // less than 2 mins
  const isWarning = secondsRemaining < 300; // less than 5 mins

  return (
    <div className="min-h-screen bg-[#f8faf9] py-6 px-3 sm:px-6 lg:px-8">
      {/* ── Intro video dialog (shown first to patient) ── */}
      <Dialog open={showVideoIntro} onOpenChange={() => { }}>
        <DialogContent className="sm:max-w-[820px] w-[92vw] p-0 border-0 shadow-2xl bg-black rounded-[2rem] overflow-hidden">
          <DialogTitle className="sr-only">Patient Introduction Video</DialogTitle>
          <DialogDescription className="sr-only">
            Watch the introduction video before starting patient registration.
          </DialogDescription>
          <div className="relative">
            <video
              ref={videoIntroRef}
              className="w-full block max-h-[75vh] object-contain bg-black cursor-pointer"
              src={PATIENT_INTRO_VIDEO_URL}
              playsInline
              onEnded={() => setShowVideoIntro(false)}
              onPlay={() => setVideoIsPlaying(true)}
              onPause={() => setVideoIsPlaying(false)}
              onClick={() => {
                const vid = videoIntroRef.current;
                if (vid) {
                  if (vid.paused) { vid.play().catch(() => { }); }
                  else { vid.pause(); }
                }
              }}
            >
              Your browser does not support embedded video.
            </video>

            {!videoIsPlaying && (
              <button
                type="button"
                onClick={() => {
                  const vid = videoIntroRef.current;
                  if (vid) vid.play().catch(() => { });
                }}
                className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/35 transition-colors"
                aria-label="Play video"
              >
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-black/70 border border-white/40 shadow-lg">
                  <Play className="w-7 h-7 text-white ml-1" fill="currentColor" />
                </span>
              </button>
            )}

            {videoNeedsUnmute && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const vid = videoIntroRef.current;
                  if (vid) vid.muted = false;
                  setVideoNeedsUnmute(false);
                }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-xl bg-black/70 hover:bg-black/85 text-white text-sm font-medium backdrop-blur-sm border border-white/40 shadow-lg transition-colors"
              >
                <VolumeX className="w-4 h-4" />
                Tap to unmute
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowVideoIntro(false);
              }}
              className="absolute top-4 right-4 px-4 py-2 rounded-xl bg-black/70 hover:bg-black/85 text-white text-sm font-medium backdrop-blur-sm border border-white/40 shadow-lg transition-colors"
            >
              Skip
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sticky Countdown Header */}
      <div className="sticky top-3 z-40 max-w-4xl mx-auto mb-6">
        <div className={cn(
          "px-4 sm:px-6 py-3.5 rounded-2xl shadow-lg border backdrop-blur-md flex flex-wrap items-center justify-between gap-3 transition-all",
          isUrgent
            ? "bg-red-500/95 text-white border-red-600 animate-pulse"
            : isWarning
            ? "bg-amber-500/95 text-white border-amber-600"
            : "bg-[#1a7a4a]/95 text-white border-[#145d39]"
        )}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold opacity-90">
                Registration Window Active
              </div>
              <div className="text-xs opacity-75 hidden sm:block">
                Valid for 10 minutes &bull; Single-use submission
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium opacity-90">Time Remaining:</span>
            <span className="font-mono text-lg sm:text-xl font-black bg-black/20 px-3 py-1 rounded-xl tracking-wider">
              {formatTimer(secondsRemaining)}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Page Title & Intro */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1a7a4a]/10 rounded-2xl flex items-center justify-center">
                <HeartHandshake className="w-5 h-5 text-[#1a7a4a]" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                  Patient Registration
                </h1>
                <p className="text-sm text-gray-500">
                  Welcome, {leadName || "Patient"}. Please review your details and accept consents below.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowVideoIntro(true)}
              className="flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3.5 py-2 rounded-xl font-semibold transition-all shadow-sm"
            >
              <Play className="w-3.5 h-3.5 fill-emerald-600 text-emerald-600" />
              <span>Replay Intro Video</span>
            </button>
          </div>
          <div className="mt-4 p-3.5 bg-blue-50 border border-blue-200 rounded-2xl text-xs sm:text-sm text-blue-800 flex items-start gap-2.5">
            <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <span>
                <strong>Phone Call Assistance:</strong> Please stay on the line with your care representative while filling out this form. If you encounter any issue or make a typo, let them know and they can resend a new link immediately.
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* SECTION 1: Personal Information */}
          <Card className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8 bg-gray-50/50 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">1. Personal & Contact Information</h2>
                <p className="text-xs text-gray-500">Demographic details and home address</p>
              </div>
            </div>

            <CardContent className="p-6 sm:p-8 space-y-6">
              {/* Name Row */}
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 sm:col-span-5 space-y-1.5">
                  <Label htmlFor="first_name" className="text-xs font-semibold text-gray-700">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="first_name"
                    placeholder="First Name"
                    className={cn("h-12 rounded-xl bg-gray-50", errors.first_name && "border-red-500 bg-red-50")}
                    {...register("first_name")}
                  />
                  {errors.first_name && (
                    <p className="text-xs text-red-500">{errors.first_name.message}</p>
                  )}
                </div>

                <div className="col-span-12 sm:col-span-2 space-y-1.5">
                  <Label htmlFor="middle_name" className="text-xs font-semibold text-gray-700">
                    M. Name
                  </Label>
                  <Input
                    id="middle_name"
                    placeholder="Middle"
                    className="h-12 rounded-xl bg-gray-50"
                    {...register("middle_name")}
                  />
                </div>

                <div className="col-span-12 sm:col-span-5 space-y-1.5">
                  <Label htmlFor="last_name" className="text-xs font-semibold text-gray-700">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="last_name"
                    placeholder="Last Name"
                    className={cn("h-12 rounded-xl bg-gray-50", errors.last_name && "border-red-500 bg-red-50")}
                    {...register("last_name")}
                  />
                  {errors.last_name && (
                    <p className="text-xs text-red-500">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* DOB, Gender, SSN */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-700">
                    Date of Birth <span className="text-red-500">*</span>
                  </Label>
                  <DatePicker
                    value={selectedDob}
                    onChange={(d: Date | undefined) => {
                      if (d) setValue("date_of_birth", d, { shouldValidate: true });
                    }}
                    placeholder="Select Date of Birth"
                    className="w-full h-12 rounded-xl bg-gray-50 border-gray-200"
                  />
                  {errors.date_of_birth && (
                    <p className="text-xs text-red-500">{errors.date_of_birth.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="gender" className="text-xs font-semibold text-gray-700">
                    Gender <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={watch("gender") || ""}
                    onValueChange={(val) => setValue("gender", val, { shouldValidate: true })}
                  >
                    <SelectTrigger id="gender" className="h-12 rounded-xl bg-gray-50">
                      <SelectValue placeholder="Select Gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.gender && (
                    <p className="text-xs text-red-500">{errors.gender.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ssn" className="text-xs font-semibold text-gray-700">
                    Social Security Number (Optional)
                  </Label>
                  <Input
                    id="ssn"
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                    className="h-12 rounded-xl bg-gray-50"
                    value={watch("social_security_number") || ""}
                    onChange={handleSsnChange}
                  />
                  {errors.social_security_number && (
                    <p className="text-xs text-red-500">{errors.social_security_number.message}</p>
                  )}
                </div>
              </div>

              {/* Contact: Phone & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="mobile_number" className="text-xs font-semibold text-gray-700">
                    Mobile Number <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="mobile_number"
                      placeholder="(555) 000-0000"
                      maxLength={14}
                      className={cn("pl-10 h-12 rounded-xl bg-gray-50", errors.mobile_number && "border-red-500 bg-red-50")}
                      value={watch("mobile_number") || ""}
                      onChange={handlePhoneChange}
                      onKeyDown={handlePhoneKeyDown}
                    />
                  </div>
                  {errors.mobile_number && (
                    <p className="text-xs text-red-500">{errors.mobile_number.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold text-gray-700">
                    Email Address (Optional)
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="patient@example.com"
                      className="pl-10 h-12 rounded-xl bg-gray-50"
                      {...register("email")}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-red-500">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <MapPin className="w-4 h-4 text-[#1a7a4a]" />
                  <span>Residential Address</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="address1" className="text-xs font-semibold text-gray-700">
                      Address Line 1 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address1"
                      placeholder="Street address"
                      className={cn("h-12 rounded-xl bg-gray-50", errors.address_line1 && "border-red-500 bg-red-50")}
                      {...register("address_line1")}
                    />
                    {errors.address_line1 && (
                      <p className="text-xs text-red-500">{errors.address_line1.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="address2" className="text-xs font-semibold text-gray-700">
                      Address Line 2 (Apt, Suite)
                    </Label>
                    <Input
                      id="address2"
                      placeholder="Apt, Suite, Bldg"
                      className="h-12 rounded-xl bg-gray-50"
                      {...register("address_line2")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="city" className="text-xs font-semibold text-gray-700">
                      City <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      placeholder="City"
                      className={cn("h-12 rounded-xl bg-gray-50", errors.city && "border-red-500 bg-red-50")}
                      {...register("city")}
                    />
                    {errors.city && <p className="text-xs text-red-500">{errors.city.message}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="state" className="text-xs font-semibold text-gray-700">
                      State <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="state"
                      placeholder="State (e.g. TX, CA)"
                      className={cn("h-12 rounded-xl bg-gray-50", errors.state && "border-red-500 bg-red-50")}
                      {...register("state")}
                    />
                    {errors.state && <p className="text-xs text-red-500">{errors.state.message}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="zip" className="text-xs font-semibold text-gray-700">
                      ZIP Code <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="zip"
                      placeholder="12345"
                      className={cn("h-12 rounded-xl bg-gray-50", errors.zip && "border-red-500 bg-red-50")}
                      {...register("zip")}
                    />
                    {errors.zip && <p className="text-xs text-red-500">{errors.zip.message}</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SECTION 2: Consents */}
          <Card className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8 bg-gray-50/50 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">2. Authorizations & Consents</h2>
                <p className="text-xs text-gray-500">HIPAA, digital records, and care communication consent</p>
              </div>
            </div>

            <CardContent className="p-6 sm:p-8 space-y-5">
              {/* HIPAA */}
              <div className={cn(
                "p-4 rounded-2xl border transition-all flex items-start gap-3.5",
                hipaaChecked ? "bg-green-50/50 border-green-200" : "bg-gray-50 border-gray-200"
              )}>
                <Checkbox
                  id="hipaa_consent"
                  checked={hipaaChecked}
                  onCheckedChange={(c) => setValue("hipaa_consent", !!c, { shouldValidate: true })}
                  className="mt-1"
                />
                <div className="space-y-1 text-xs sm:text-sm">
                  <Label htmlFor="hipaa_consent" className="font-semibold text-gray-900 cursor-pointer">
                    HIPAA Privacy Notice &amp; Medical Information Consent <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-gray-600 leading-relaxed text-xs">
                    I acknowledge that I have received and understand the Notice of Privacy Practices regarding the use and disclosure of my protected health information (PHI) in compliance with HIPAA regulations.
                  </p>
                  {errors.hipaa_consent && (
                    <p className="text-xs text-red-500 font-medium">{errors.hipaa_consent.message}</p>
                  )}
                </div>
              </div>

              {/* eSign */}
              <div className={cn(
                "p-4 rounded-2xl border transition-all flex items-start gap-3.5",
                esignChecked ? "bg-green-50/50 border-green-200" : "bg-gray-50 border-gray-200"
              )}>
                <Checkbox
                  id="esign_consent"
                  checked={esignChecked}
                  onCheckedChange={(c) => setValue("esign_consent", !!c, { shouldValidate: true })}
                  className="mt-1"
                />
                <div className="space-y-1 text-xs sm:text-sm">
                  <Label htmlFor="esign_consent" className="font-semibold text-gray-900 cursor-pointer">
                    Electronic Signature (e-Sign) &amp; Digital Records Authorization <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-gray-600 leading-relaxed text-xs">
                    I consent to the use of electronic records, communications, and digital signatures. I understand my submission here carries the legal equivalent of a handwritten signature.
                  </p>
                  {errors.esign_consent && (
                    <p className="text-xs text-red-500 font-medium">{errors.esign_consent.message}</p>
                  )}
                </div>
              </div>

              {/* MonitorHealth Agreement */}
              <div className={cn(
                "p-4 rounded-2xl border transition-all flex items-start gap-3.5",
                mhaiChecked ? "bg-green-50/50 border-green-200" : "bg-gray-50 border-gray-200"
              )}>
                <Checkbox
                  id="mhai_consent"
                  checked={mhaiChecked}
                  onCheckedChange={(c) => setValue("mhai_consent", !!c, { shouldValidate: true })}
                  className="mt-1"
                />
                <div className="space-y-1 text-xs sm:text-sm">
                  <Label htmlFor="mhai_consent" className="font-semibold text-gray-900 cursor-pointer">
                    MonitorHealth AI Remote Health Monitoring Consent <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-gray-600 leading-relaxed text-xs">
                    I authorize participation in remote clinical health monitoring and agree to receive personalized care assistance and updates from the clinic care team.
                  </p>
                  {errors.mhai_consent && (
                    <p className="text-xs text-red-500 font-medium">{errors.mhai_consent.message}</p>
                  )}
                </div>
              </div>

              {/* SMS Consent */}
              <div className={cn(
                "p-4 rounded-2xl border transition-all flex items-start gap-3.5",
                smsChecked ? "bg-green-50/50 border-green-200" : "bg-gray-50 border-gray-200"
              )}>
                <Checkbox
                  id="sms_consent"
                  checked={smsChecked}
                  onCheckedChange={(c) => setValue("sms_consent", !!c, { shouldValidate: true })}
                  className="mt-1"
                />
                <div className="space-y-1 text-xs sm:text-sm">
                  <Label htmlFor="sms_consent" className="font-semibold text-gray-900 cursor-pointer">
                    SMS Text Messaging Consent <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-gray-600 leading-relaxed text-xs">
                    I agree to receive transactional text messages, appointment notifications, and care surveys at the mobile number provided above. Standard carrier rates may apply.
                  </p>
                  {errors.sms_consent && (
                    <p className="text-xs text-red-500 font-medium">{errors.sms_consent.message}</p>
                  )}
                </div>
              </div>

              {/* Biometric Consent */}
              <div className={cn(
                "p-4 rounded-2xl border transition-all flex items-start gap-3.5",
                biometricChecked ? "bg-green-50/50 border-green-200" : "bg-gray-50 border-gray-200"
              )}>
                <Checkbox
                  id="biometric_consent"
                  checked={biometricChecked}
                  onCheckedChange={(c) => setValue("biometric_consent", !!c, { shouldValidate: true })}
                  className="mt-1"
                />
                <div className="space-y-1 text-xs sm:text-sm">
                  <Label htmlFor="biometric_consent" className="font-semibold text-gray-900 cursor-pointer">
                    Biometric Consent <span className="text-gray-400 font-normal">(optional)</span>
                  </Label>
                  <p className="text-gray-600 leading-relaxed text-xs">
                    I consent to the collection and use of my biometric data (facial recognition) for identity verification and access to healthcare services.
                  </p>
                </div>
              </div>

              {/* Email Consent (if email provided) */}
              {emailVal && emailVal.trim() !== "" && (
                <div className={cn(
                  "p-4 rounded-2xl border transition-all flex items-start gap-3.5",
                  emailChecked ? "bg-green-50/50 border-green-200" : "bg-gray-50 border-gray-200"
                )}>
                  <Checkbox
                    id="email_consent"
                    checked={emailChecked}
                    onCheckedChange={(c) => setValue("email_consent", !!c, { shouldValidate: true })}
                    className="mt-1"
                  />
                  <div className="space-y-1 text-xs sm:text-sm">
                    <Label htmlFor="email_consent" className="font-semibold text-gray-900 cursor-pointer">
                      Email Communication Consent <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-gray-600 leading-relaxed text-xs">
                      I agree to receive health updates, appointment confirmations, and clinic summaries at the email address provided.
                    </p>
                    {errors.email_consent && (
                      <p className="text-xs text-red-500 font-medium">{errors.email_consent.message}</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Action */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm text-center space-y-4">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <ShieldCheck className="w-4 h-4 text-[#1a7a4a]" />
              <span>Single-use submission &bull; Encrypted &bull; HIPAA Compliant</span>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto min-w-[280px] h-14 text-base font-bold bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-2xl shadow-lg shadow-green-900/10 transition-all flex items-center justify-center gap-2 mx-auto"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Submitting to Care Team...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Submit Patient Registration</span>
                </>
              )}
            </Button>

            <p className="text-xs text-gray-400 max-w-md mx-auto">
              Once submitted, your record will be placed in the registration queue for your on-call coordinator to review and complete.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
